/* eslint new-cap: ["error", { "newIsCapExceptions": ["aesjs.ModeOfOperation.ctr"] }] */
import aesjs from 'aes-js';
import Cookie from 'js-cookie';
import Console from './console';
import storage from './fallback_storage';

const console = new Console(window.Settings.SESSIONWIDGET_DEBUG);

const KEY_EXPIRATION_TIME = 14; // day
const AUTH_STATE = {
    UNAUTHORIZED: 0,
    SESSION: 1,
    PERMANENT: 2
};
const cachedKey = {
    raw: null,
    key: null
};

class TokenStorage {
    constructor(settings = {}) {
        this.debug = settings.SESSIONWIDGET_DEBUG || false;

        this.tokenKeyPrefix = window.Settings.SESSIONWIDGET_KEY_PREFIX;
        this.storage = storage;

        this.isAuthenticatedCookieName = settings.IS_AUTHENTICATED_COOKIE_NAME;
        this.sessionExpirationCookieName = `${settings.IS_AUTHENTICATED_COOKIE_NAME}_expiration`;
        this.storageKeyCookieName = settings.SESSIONWIDGET_STORAGE_KEY_COOKIE_NAME;
        this.storageKeyCookiePath = settings.SESSIONWIDGET_STORAGE_KEY_COOKIE_PATH;
    }

    get authenticatedState() {
        const isAuthenticated = Cookie.get(this.isAuthenticatedCookieName);

        if (isAuthenticated !== 'yes') {
            return AUTH_STATE.UNAUTHORIZED;
        }
        const expirationType = Cookie.get(this.sessionExpirationCookieName);

        return expirationType === 'session' ? AUTH_STATE.SESSION : AUTH_STATE.PERMANENT;
    }

    get newEncryptionKey() {
        const options = {
            path: this.storageKeyCookiePath,
            expires: this.authenticatedState !== AUTH_STATE.SESSION ? KEY_EXPIRATION_TIME : null
        };

        cachedKey.key = [];
        for (let i = 0; i < 32; i += 1) {
            cachedKey.key.push(Math.floor(Math.random() * 256));
        }

        Cookie.set(this.storageKeyCookieName, cachedKey.key, options);
        cachedKey.raw = Cookie.get(this.storageKeyCookieName);
        console.log(cachedKey);
        return cachedKey.key;
    }

    get encryptionKey() {
        const password = Cookie.get(this.storageKeyCookieName);

        if (password === cachedKey.raw) {
            return cachedKey.key;
        }
        if (password) {
            try {
                cachedKey.key = JSON.parse(password);
                cachedKey.raw = password;
            } catch (e) {
                return this.newEncryptionKey;
            }
            return cachedKey.key;
        }
        return this.newEncryptionKey;
    }

    get aesCtr() {
        return new aesjs.ModeOfOperation.ctr(this.encryptionKey, new aesjs.Counter(5));
    }

    encryptText(text = '') {
        const textBytes = aesjs.util.convertStringToBytes(text);
        const encrypted = this.aesCtr.encrypt(textBytes);

        return encrypted;
    }

    decryptText(encrypted = '') {
        const decryptedBytes = this.aesCtr.decrypt(encrypted);

        return aesjs.util.convertBytesToString(decryptedBytes);
    }

    encryptTokenData(data = {}) {
        const text = JSON.stringify(data);

        return this.encryptText(text);
    }

    decryptTokenData(encryptedText) {
        const text = this.decryptText(encryptedText);
        let data;

        try {
            data = JSON.parse(text);
        } catch (e) {
            console.log('Widget: decrypted json parse error: ', e);
        }
        return data;
    }

    getTokenKey(clientId, scope) {
        const scopeSuffix = scope ? `_${scope}` : '';

        return `${this.tokenKeyPrefix}${clientId}${scopeSuffix}`;
    }

    filter(clientId = '', scope) {
        const prefix = this.getTokenKey(clientId);
        const tokens = Object.keys(this.storage).filter(key => {
            if (key.indexOf(prefix) === 0) {
                let item;

                if (key === prefix) {
                    return false;
                }
                try {
                    item = JSON.parse(this.storage.getItem(key));
                } catch (e) {
                    console.log('Widget: stored token parse error: ', e);
                }

                if (!item) {
                    this.storage.removeItem(key);
                    return false;
                }

                if (scope && item.scope && item.scope.indexOf(scope) === -1) {
                    return false;
                }
                return true;
            }
            return false;
        });

        return tokens.map(this.getByKey.bind(this));
    }

    get(clientId, scope) {
        const keyScope = scope || this.storage.getItem(this.getTokenKey(clientId));

        if (!keyScope) {
            return null;
        }

        const key = this.getTokenKey(clientId, keyScope);

        return this.getByKey(key);
    }

    getByKey(key, forsedAll = false) {
        const stored = this.storage.getItem(key);

        if (!stored) {
            return null;
        }

        let parsed;
        let token;

        try {
            parsed = JSON.parse(stored);
            token = this.decryptTokenData(parsed.token);
        } catch (e) {
            console.warn('Widget: stored token parse error', stored.token);
        }

        if (!token) {
            this.storage.removeItem(key);
            return null;
        }

        if (forsedAll) {
            return token;
        }

        if (this.authenticatedState > AUTH_STATE.UNAUTHORIZED) {
            if (parsed.expires_at > Date.now()) {
                console.log('Widget: stored token found: ', token);
                return token;
            }
            this.storage.removeItem(key);
            console.warn('Widget: stored token expired: ', token);
            return null;
        }
        if (token.error && token.error === 'access_denied') {
            console.log('Widget: stored token error found:', token);
            return token;
        }
        this.storage.removeItem(key);
        return null;
    }

    set(clientId, scope, data) {
        const dataScope = data.scope || 'full_scope';
        const key = this.getTokenKey(clientId, dataScope);

        if (!scope) {
            const aliasKey = this.getTokenKey(clientId);

            this.storage.setItem(aliasKey, dataScope);
        }
        const expiresIn = parseInt(data.expires_in, 10);
        const store = {
            scope: dataScope,
            token: this.encryptTokenData(data)
        };

        if (expiresIn) {
            store.expires_at = Date.now() + (expiresIn * 1000);
        }
        this.storage.setItem(key, JSON.stringify(store));
        console.log('Widget: stored data', store);
    }

    remove(clientId, scope) {
        let key;

        if (scope) {
            key = this.getTokenKey(clientId, scope);
        } else {
            const aliasKey = this.getTokenKey(clientId);
            const aliasScope = this.storage.getItem(aliasKey);

            key = this.getTokenKey(clientId, aliasScope);
            this.storage.removeItem(aliasKey);
        }
        const token = this.getByKey(key);

        this.storage.removeItem(key);
        return token;
    }
}

export default TokenStorage;



// WEBPACK FOOTER //
// ./static_source/js/token_storage.js